<template>
  <div>
    <header-component />
    <div class="container-fluid ">
      <div class="overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm">
        <div
          class="flex flex-wrap gap-3 justify-between items-center px-4 py-4 border-b border-slate-200 sm:px-6"
        >
          <div>
            <h3 class="text-lg font-semibold tracking-tight text-slate-900">
              Vendor reports
            </h3>
            <p class="mt-0.5 text-sm text-slate-500">
              {{ vendorDisplayName }}
            </p>
          </div>
          <base-button size="sm" type="secondary" @click="goToVendorList">
            ← Vendors list
          </base-button>
        </div>

        <div class="px-4 py-6 sm:px-6">
          <!-- Drill breadcrumb -->
          <div v-if="drillStep > 0" class="flex flex-wrap gap-1 items-center mb-4">
            <el-button type="text" size="small" class="!p-0" @click="drillBackAll">
              Summary
            </el-button>
            <span v-if="reportLabel" class="text-slate-400">/</span>
            <el-button
              v-if="reportLabel && drillStep > 1"
              type="text"
              size="small"
              class="!p-0"
              @click="drillBackToMonths"
            >
              {{ reportLabel }}
            </el-button>
            <span v-if="selectedMonthTitle && drillStep > 2" class="text-slate-400"
              >/</span
            >
            <el-button
              v-if="selectedMonthTitle && drillStep > 2"
              type="text"
              size="small"
              class="!p-0"
              @click="drillBackToDays"
            >
              {{ selectedMonthTitle }}
            </el-button>
            <span v-if="selectedDayLabel && drillStep === 3" class="text-slate-400"
              >/</span
            >
            <span
              v-if="selectedDayLabel && drillStep === 3"
              class="text-sm font-medium text-slate-700"
            >
              {{ selectedDayLabel }}
            </span>
          </div>

          <!-- Summary -->
          <div v-if="drillStep === 0" v-loading="loadingCounts">
            <div
              class="p-1 mb-6 rounded-xl bg-slate-200/90 ring-1 ring-slate-300/60"
            >
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left rounded-t-lg transition hover:bg-white/60"
                @click="startReport('revenue')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Monthly billed work (invoice)</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹ {{ formatMoney(counts.serviceRequestLaborTotal) }}</span
                >
              </button>
              <div class="mx-3 h-px bg-slate-300/80" />
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left transition hover:bg-white/60"
                @click="startReport('transactions')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Monthly received (completed)</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹
                  {{
                    formatMoney(counts.monthlyLaborTotalForClosedServiceRequests)
                  }}</span
                >
              </button>
              <div class="mx-3 h-px bg-slate-300/80" />
              <button
                type="button"
                class="flex justify-between items-center px-4 py-3 w-full text-left rounded-b-lg transition hover:bg-white/60"
                @click="startReport('outstanding')"
              >
                <span class="text-sm font-medium text-slate-800"
                  >Outstanding</span
                >
                <span class="text-sm font-bold tabular-nums text-slate-900"
                  >₹ {{ formatMoney(counts.outstanding) }}</span
                >
              </button>
            </div>

            <div class="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-3">
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Ongoing requests
                </div>
                <div class="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                  {{ counts.ongoingServiceRequests || 0 }}
                </div>
              </div>
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Points (month)
                </div>
                <div
                  class="mt-1 text-2xl font-semibold tabular-nums text-emerald-600"
                >
                  {{ counts.vendorPoints || 0 }}
                </div>
              </div>
              <div class="px-4 py-3 rounded-lg border bg-slate-50 border-slate-200">
                <div class="text-xs font-medium tracking-wide uppercase text-slate-500">
                  Penalty (month)
                </div>
                <div class="mt-1 text-2xl font-semibold tabular-nums text-red-600">
                  {{ counts.vendorPenalty || 0 }}
                </div>
              </div>
            </div>

            <p class="text-xs leading-relaxed text-slate-500">
              Tap a summary row to view month-wise figures, then a month for
              day-wise totals, then a day for request-level detail (same flow as
              the vendor mobile app).
            </p>
          </div>

          <!-- Tables -->
          <div v-else v-loading="loadingDetail">
            <div class="flex justify-between items-center mb-4">
              <h4 class="text-base font-semibold text-slate-900">
                {{ reportTitle }}
              </h4>
              <el-button size="small" @click="drillBack">← Back</el-button>
            </div>

            <div class="overflow-x-auto rounded-xl border border-slate-200">
              <table
                v-if="drillStep === 1"
                class="min-w-full text-sm divide-y divide-slate-200"
              >
                <thead class="bg-slate-100 text-slate-700">
                  <tr>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">Period</th>
                    <th v-if="reportKind === 'revenue'" class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Labor</th>
                    <th v-if="reportKind === 'revenue'" class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">External</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Total / Amount</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Requests</th>
                    <th v-if="reportKind === 'outstanding'" class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Rajput Bill</th>
                    <th v-if="reportKind === 'outstanding'" class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Rajdoot Bill</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <tr
                    v-for="row in monthRows"
                    :key="`month-${row.title}`"
                    class="cursor-pointer hover:bg-blue-50"
                    @click="onMonthRowClick(row)"
                  >
                    <td class="px-4 py-3 font-medium">{{ row.title }}</td>
                    <td v-if="reportKind === 'revenue'" class="px-4 py-3 text-right">₹ {{ formatMoney(row.labor) }}</td>
                    <td v-if="reportKind === 'revenue'" class="px-4 py-3 text-right">₹ {{ formatMoney(row.externalGoods) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(rowMonthTotal(row)) }}</td>
                    <td class="px-4 py-3 text-right">{{ row.count || 0 }}</td>
                    <td v-if="reportKind === 'outstanding'" class="px-4 py-3 text-right">₹ {{ formatMoney(row.rajputBill) }}</td>
                    <td v-if="reportKind === 'outstanding'" class="px-4 py-3 text-right">₹ {{ formatMoney(row.rajdootBill) }}</td>
                  </tr>
                  <tr v-if="monthRows.length" class="font-semibold bg-slate-50">
                    <td class="px-4 py-3">Total</td>
                    <td v-if="reportKind === 'revenue'" class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumBy(monthRows, 'labor')) }}
                    </td>
                    <td v-if="reportKind === 'revenue'" class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumBy(monthRows, 'externalGoods')) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumMonthRowsTotal()) }}
                    </td>
                    <td class="px-4 py-3 text-right">
                      {{ sumBy(monthRows, 'count') }}
                    </td>
                    <td v-if="reportKind === 'outstanding'" class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumBy(monthRows, 'rajputBill')) }}
                    </td>
                    <td v-if="reportKind === 'outstanding'" class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumBy(monthRows, 'rajdootBill')) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                v-else-if="drillStep === 2"
                class="min-w-full text-sm divide-y divide-slate-200"
              >
                <thead class="bg-slate-100 text-slate-700">
                  <tr>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">Day</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Amount / Total</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Requests</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <tr
                    v-for="row in dayRows"
                    :key="`day-${row.title}`"
                    class="cursor-pointer hover:bg-blue-50"
                    @click="onDayRowClick(row)"
                  >
                    <td class="px-4 py-3 font-medium">{{ row.title }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(rowDayTotal(row)) }}</td>
                    <td class="px-4 py-3 text-right">{{ row.count || 0 }}</td>
                  </tr>
                  <tr v-if="dayRows.length" class="font-semibold bg-slate-50">
                    <td class="px-4 py-3">Total</td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(sumDayRowsTotal()) }}
                    </td>
                    <td class="px-4 py-3 text-right">{{ sumBy(dayRows, 'count') }}</td>
                  </tr>
                </tbody>
              </table>

              <table v-else class="min-w-full text-sm divide-y divide-slate-200">
                <thead class="bg-slate-100 text-slate-700">
                  <tr>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">SR No.</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">Customer</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">Phone</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-left uppercase">Status</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Labor</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">External</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Chalan</th>
                    <th class="px-4 py-2 text-xs font-semibold tracking-wider text-right uppercase">Total</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-slate-200">
                  <tr
                    v-for="row in requestRows"
                    :key="`req-${row.serviceRequestId || row.serviceRequestNumber}`"
                    class="hover:bg-slate-50"
                  >
                    <td class="px-4 py-3">
                      <router-link
                        v-if="row.serviceRequestId"
                        :to="'/serviceRequests/flow/' + row.serviceRequestId"
                        class="font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        {{ getServiceRequestNumber(row) }}
                      </router-link>
                      <span v-else>{{ getServiceRequestNumber(row) }}</span>
                    </td>
                    <td class="px-4 py-3">{{ getCustomerDisplay(row) }}</td>
                    <td class="px-4 py-3 whitespace-nowrap">{{ getPhoneDisplay(row) }}</td>
                    <td class="px-4 py-3">
                      <span
                        class="inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border whitespace-nowrap"
                        :style="statusBadgeStyle(row.status)"
                      >
                        {{ getStatusLabel(row.status) }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(row.labor) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(row.externalGoods) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(row.chalans) }}</td>
                    <td class="px-4 py-3 text-right">
                      ₹ {{ formatMoney(num(row.labor) + num(row.externalGoods) + num(row.chalans)) }}
                    </td>
                  </tr>
                  <tr v-if="requestRows.length" class="font-semibold bg-slate-50">
                    <td class="px-4 py-3" colspan="4">Total</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(sumBy(requestRows, 'labor')) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(sumBy(requestRows, 'externalGoods')) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(sumBy(requestRows, 'chalans')) }}</td>
                    <td class="px-4 py-3 text-right">₹ {{ formatMoney(sumRequestRowsTotal()) }}</td>
                  </tr>
                  <tr v-if="requestRows.length === 0">
                    <td colspan="8" class="px-4 py-6 text-sm text-center text-slate-500">
                      No request details found for this day.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Button } from "element-ui";
import HeaderComponent from "../Components/HeaderComponent";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import snakeToCamel from "@/utils/SnakeToCamel";

export default {
  name: "VendorReports",
  components: {
    [Button.name]: Button,
    "header-component": HeaderComponent
  },
  data() {
    return {
      counts: {},
      loadingCounts: false,
      loadingDetail: false,
      drillStep: 0,
      reportKind: null,
      monthRows: [],
      dayRows: [],
      requestRows: [],
      selectedMonthTitle: null,
      selectedDayLabel: null
    };
  },
  computed: {
    vendorId() {
      return this.$route.params.vendorId;
    },
    vendorDisplayName() {
      const q = this.$route.query.name;
      return q ? String(q) : "Vendor";
    },
    reportLabel() {
      if (this.reportKind === "revenue") return "Monthly revenue";
      if (this.reportKind === "transactions") return "Monthly transactions";
      if (this.reportKind === "outstanding") return "Monthly outstanding";
      return "";
    },
    reportTitle() {
      if (this.drillStep === 1) return this.reportLabel;
      if (this.drillStep === 2)
        return `${this.reportLabel} — ${this.selectedMonthTitle || ""}`;
      return `Request detail — ${this.selectedDayLabel || ""}`;
    }
  },
  watch: {
    vendorId: {
      immediate: true,
      handler() {
        this.resetDrill();
        this.loadCounts();
      }
    }
  },
  methods: {
    baseUrl() {
      return `${endpoints.LARAVEL_BASE_URL}/api/v1`;
    },
    todayParam() {
      const d = new Date();
      const p = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    num(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    formatMoney(v) {
      const n = this.num(v);
      return n.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },
    normalizeRows(data) {
      const raw = Array.isArray(data) ? data : [];
      return snakeToCamel(raw);
    },
    rowMonthTotal(row) {
      if (this.reportKind === "revenue") {
        return this.num(row.totalSum != null ? row.totalSum : row.total_sum);
      }
      if (this.reportKind === "transactions") {
        return this.num(row.sum);
      }
      return this.num(row.sum);
    },
    rowDayTotal(row) {
      if (this.reportKind === "outstanding") {
        return this.num(row.sum);
      }
      if (this.reportKind === "transactions") {
        return this.num(row.sum);
      }
      return this.num(
        row.totalSum != null ? row.totalSum : row.total_sum != null
          ? row.total_sum
          : row.sum
      );
    },
    sumBy(rows, key) {
      return (rows || []).reduce((acc, r) => acc + this.num(r[key]), 0);
    },
    sumMonthRowsTotal() {
      return (this.monthRows || []).reduce((acc, r) => acc + this.rowMonthTotal(r), 0);
    },
    sumDayRowsTotal() {
      return (this.dayRows || []).reduce((acc, r) => acc + this.rowDayTotal(r), 0);
    },
    sumRequestRowsTotal() {
      return (this.requestRows || []).reduce(
        (acc, r) => acc + this.num(r.labor) + this.num(r.externalGoods) + this.num(r.chalans),
        0
      );
    },
    getServiceRequestNumber(row) {
      return (
        row.serviceRequestNumber ||
        row.serviceRequestNo ||
        row.identifier ||
        "-"
      );
    },
    getCustomerDisplay(row) {
      const candidate =
        row.customer ||
        row.customerName ||
        row.customer_name ||
        row.userName ||
        row.name ||
        "";
      const text = String(candidate).trim();
      const normalized = text.replace(/\s+/g, " ").toLowerCase();
      if (
        !text ||
        normalized === "0" ||
        normalized === "₹ 0" ||
        normalized === "rs 0" ||
        normalized === "null" ||
        normalized === "undefined" ||
        normalized === "n/a"
      ) {
        return "-";
      }
      return text;
    },
    getPhoneDisplay(row) {
      const val = row.phone || row.customerPhone || row.mobile || "";
      const text = String(val).replace(/\s+/g, "").trim();
      return text || "-";
    },
    getStatusLabel(status) {
      const text = status == null ? "" : String(status).trim();
      return text || "-";
    },
    statusBadgeStyle(status) {
      const key = String(status || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "_");

      if (["completed", "done"].includes(key)) {
        return {
          color: "#047857",
          backgroundColor: "#ecfdf5",
          borderColor: "#a7f3d0"
        };
      }
      if (["payment_pending", "outstanding"].includes(key)) {
        return {
          color: "#b45309",
          backgroundColor: "#fffbeb",
          borderColor: "#fde68a"
        };
      }
      if (["billing", "work_in_progress", "processing"].includes(key)) {
        return {
          color: "#1d4ed8",
          backgroundColor: "#eff6ff",
          borderColor: "#bfdbfe"
        };
      }
      if (["quotation_pending", "pending"].includes(key)) {
        return {
          color: "#c2410c",
          backgroundColor: "#fff7ed",
          borderColor: "#fdba74"
        };
      }
      if (["quotation_completed", "visited", "assigned"].includes(key)) {
        return {
          color: "#3730a3",
          backgroundColor: "#eef2ff",
          borderColor: "#c7d2fe"
        };
      }
      if (["cancelled", "canceled", "rejected"].includes(key)) {
        return {
          color: "#be123c",
          backgroundColor: "#fff1f2",
          borderColor: "#fecdd3"
        };
      }
      return {
        color: "#334155",
        backgroundColor: "#f8fafc",
        borderColor: "#cbd5e1"
      };
    },
    goToVendorList() {
      this.$router.push("/vendors/list");
    },
    resetDrill() {
      this.drillStep = 0;
      this.reportKind = null;
      this.monthRows = [];
      this.dayRows = [];
      this.requestRows = [];
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
    },
    async loadCounts() {
      if (!this.vendorId) return;
      this.loadingCounts = true;
      try {
        const res = await apiHelper.call(
          "get",
          `${this.baseUrl()}/vendors/${this.vendorId}/counts?date=${this.todayParam()}`
        );
        const body = res.data;
        this.counts =
          body && typeof body === "object" && !Array.isArray(body)
            ? snakeToCamel(body)
            : {};
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load vendor summary."
        });
        this.counts = {};
      } finally {
        this.loadingCounts = false;
      }
    },
    parseMonthYear(title) {
      if (!title) return null;
      const parts = String(title).trim().split(/\s+/);
      if (parts.length < 2) return null;
      return { month: parts[0], year: parts[1] };
    },
    startReport(kind) {
      this.reportKind = kind;
      this.drillStep = 1;
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
      this.dayRows = [];
      this.requestRows = [];
      this.loadMonthwise();
    },
    async loadMonthwise() {
      this.loadingDetail = true;
      try {
        let url;
        if (this.reportKind === "revenue") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/revenue/monthwise`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/transactions/monthwise`;
        } else {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/outstanding/monthwise`;
        }
        const res = await apiHelper.call("get", url);
        this.monthRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load month-wise data."
        });
        this.monthRows = [];
      } finally {
        this.loadingDetail = false;
      }
    },
    onMonthRowClick(row) {
      const my = this.parseMonthYear(row.title);
      if (!my) {
        this.$notify({
          type: "warning",
          title: "Parse error",
          message: "Could not read month from row."
        });
        return;
      }
      this.selectedMonthTitle = row.title;
      this.drillStep = 2;
      this.loadDaywise(my.month, my.year);
    },
    async loadDaywise(month, year) {
      this.loadingDetail = true;
      this.dayRows = [];
      try {
        let url;
        if (this.reportKind === "revenue") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/revenue/daywise/${encodeURIComponent(month)}/${encodeURIComponent(year)}`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/transactions/daywise/${encodeURIComponent(month)}/${encodeURIComponent(year)}`;
        } else {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/outstanding/daywise/${encodeURIComponent(month)}/${encodeURIComponent(year)}`;
        }
        const res = await apiHelper.call("get", url);
        this.dayRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load day-wise data."
        });
      } finally {
        this.loadingDetail = false;
      }
    },
    onDayRowClick(row) {
      const date = row.title;
      if (!date) return;
      this.selectedDayLabel = date;
      this.drillStep = 3;
      this.loadDayRequests(date);
    },
    async loadDayRequests(date) {
      this.loadingDetail = true;
      this.requestRows = [];
      try {
        let url;
        const enc = encodeURIComponent(date);
        if (this.reportKind === "revenue") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/revenue/day/${enc}`;
        } else if (this.reportKind === "transactions") {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/transactions/day/${enc}`;
        } else {
          url = `${this.baseUrl()}/vendors/${this.vendorId}/outstanding/day/${enc}`;
        }
        const res = await apiHelper.call("get", url);
        this.requestRows = this.normalizeRows(res.data);
      } catch (e) {
        this.$notify({
          type: "danger",
          title: "Error",
          message: "Could not load requests for this day."
        });
      } finally {
        this.loadingDetail = false;
      }
    },
    drillBack() {
      if (this.drillStep === 3) {
        this.drillStep = 2;
        this.selectedDayLabel = null;
        this.requestRows = [];
        return;
      }
      if (this.drillStep === 2) {
        this.drillStep = 1;
        this.selectedMonthTitle = null;
        this.dayRows = [];
        return;
      }
      if (this.drillStep === 1) {
        this.resetDrill();
      }
    },
    drillBackToMonths() {
      this.drillStep = 1;
      this.selectedMonthTitle = null;
      this.selectedDayLabel = null;
      this.dayRows = [];
      this.requestRows = [];
    },
    drillBackToDays() {
      this.drillStep = 2;
      this.selectedDayLabel = null;
      this.requestRows = [];
    },
    drillBackAll() {
      this.resetDrill();
    }
  }
};
</script>

<style scoped></style>
